import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';
import { 
  NavigationMenu, 
  NavigationMenuContent, 
  NavigationMenuItem, 
  NavigationMenuLink, 
  NavigationMenuList, 
  NavigationMenuTrigger 
} from '@/components/ui/navigation-menu';
import { 
  Menu, Globe, Plane, Users, Building2, Phone, UserPlus, Sparkles, 
  ChevronDown, Briefcase, Code, BookOpen, HeadphonesIcon, Scale,
  Home, Play, Calendar, Handshake, Bell, Award, History, Users2,
  MessageSquare, Shield, Cpu, Hotel, FileText, Ship, Train, Car,
  Wallet, Smartphone, Leaf, Stethoscope, Ticket, Coffee, Zap,
  Bus, MapPin, CreditCard, Package, Globe2, Building, GraduationCap
} from 'lucide-react';

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();

  const isActive = (path) => location.pathname === path;

  // Navigation structure based on sitemap
  const navStructure = {
    about: {
      label: 'About Osaka',
      items: [
        { name: 'About Osaka Group', path: '/about/osaka-group', icon: Building2 },
        { name: 'Legacy Since 1993', path: '/about/legacy', icon: History },
        { name: 'Group Companies', path: '/about/companies', icon: Building },
        { name: 'Chairman Message', path: '/about/chairman', icon: MessageSquare },
        { name: 'Leadership Team', path: '/about/leadership', icon: Users2 },
        { name: 'Awards & Recognition', path: '/about/awards', icon: Award },
      ]
    },
    platform: {
      label: 'Platform',
      items: [
        { name: 'What is Travel Hypermarket', path: '/platform/hypermarket', icon: Globe },
        { name: 'Why OsakaConnect', path: '/platform/why-osaka', icon: Sparkles },
        { name: 'Platform Architecture', path: '/platform/architecture', icon: Cpu },
        { name: 'AI Travel Intelligence', path: '/ai-tools', icon: Sparkles },
        { name: 'Security & Compliance', path: '/platform/security', icon: Shield },
      ]
    },
    products: {
      label: 'Products',
      items: [
        { name: 'Flights', path: '/products/flights', icon: Plane },
        { name: 'Hotels', path: '/products/hotels', icon: Hotel },
        { name: 'Visa Services', path: '/products/visa', icon: FileText },
        { name: 'Travel Insurance', path: '/products/insurance', icon: Shield },
        { name: 'Cruise Booking', path: '/products/cruise', icon: Ship },
        { name: 'Activities & Experiences', path: '/products/activities', icon: Ticket },
        { name: 'View All 24 Products', path: '/products', icon: Package },
      ]
    },
    partners: {
      label: 'Partners',
      items: [
        { name: 'B2B Travel Agents', path: '/partners/b2b', icon: Users },
        { name: 'B2E Corporate Travel', path: '/partners/b2e', icon: Building2 },
        { name: 'API Partners', path: '/partners/api', icon: Code },
        { name: 'Strategic Partners', path: '/partners/strategic', icon: Handshake },
        { name: 'White Label Platform', path: '/partners/whitelabel', icon: Globe2 },
        { name: 'Become a Partner', path: '/signup', icon: UserPlus },
      ]
    },
    resources: {
      label: 'Resources',
      items: [
        { name: 'Developer Portal', path: '/developers', icon: Code },
        { name: 'Case Studies', path: '/resources/case-studies', icon: BookOpen },
        { name: 'Blog & Insights', path: '/resources/blog', icon: FileText },
        { name: 'Help Center', path: '/support/help', icon: HeadphonesIcon },
        { name: 'Training Academy', path: '/support/training', icon: GraduationCap },
        { name: 'Download Center', path: '/resources/downloads', icon: Package },
      ]
    },
  };

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-lg border-b border-slate-100">
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-18 lg:h-22">
          {/* Logo */}
          <Link to="/" className="flex-shrink-0 flex items-center" data-testid="logo-link">
            <img 
              src="/osaka-logo.svg" 
              alt="OsakaConnect - Travel Hypermarket" 
              className="h-12 lg:h-16 w-auto"
            />
          </Link>

          {/* Desktop Navigation with Mega Menu */}
          <div className="hidden lg:flex items-center ml-8">
            <NavigationMenu>
              <NavigationMenuList className="gap-0">
                {Object.entries(navStructure).map(([key, section]) => (
                  <NavigationMenuItem key={key}>
                    <NavigationMenuTrigger className="px-3 py-2 text-sm font-medium text-slate-600 hover:text-[#004E89] bg-transparent hover:bg-slate-50 data-[state=open]:bg-slate-50">
                      {section.label}
                    </NavigationMenuTrigger>
                    <NavigationMenuContent>
                      <div className="w-[400px] p-4 bg-white rounded-xl shadow-xl border border-slate-100">
                        <div className="grid gap-2">
                          {section.items.map((item) => {
                            const Icon = item.icon;
                            return (
                              <Link
                                key={item.path}
                                to={item.path}
                                className="flex items-center gap-3 p-3 rounded-lg hover:bg-[#E5F4F5] transition-colors group"
                              >
                                <div className="w-10 h-10 rounded-lg bg-slate-100 group-hover:bg-[#004E89]/10 flex items-center justify-center">
                                  <Icon className="w-5 h-5 text-[#004E89]" />
                                </div>
                                <span className="text-sm font-medium text-slate-700 group-hover:text-[#004E89]">
                                  {item.name}
                                </span>
                              </Link>
                            );
                          })}
                        </div>
                      </div>
                    </NavigationMenuContent>
                  </NavigationMenuItem>
                ))}
                
                {/* Direct Links */}
                <NavigationMenuItem>
                  <Link 
                    to="/global-presence"
                    className="inline-flex items-center whitespace-nowrap px-3 py-2 text-sm font-medium text-slate-600 hover:text-[#004E89] hover:bg-slate-50 rounded-md transition-colors"
                  >
                    Global Offices
                  </Link>
                </NavigationMenuItem>
                <NavigationMenuItem>
                  <Link 
                    to="/contact"
                    className="inline-flex items-center whitespace-nowrap px-3 py-2 text-sm font-medium text-slate-600 hover:text-[#004E89] hover:bg-slate-50 rounded-md transition-colors"
                  >
                    Contact
                  </Link>
                </NavigationMenuItem>
              </NavigationMenuList>
            </NavigationMenu>
          </div>

          {/* CTA Buttons */}
          <div className="hidden lg:flex items-center gap-3 flex-shrink-0 ml-4">
            <Link to="/demo">
              <Button 
                variant="outline"
                data-testid="demo-btn"
                className="rounded-full px-5 h-10 border-[#004E89]/20 text-[#004E89] hover:bg-[#E5F4F5] font-medium text-sm whitespace-nowrap"
              >
                <Play className="w-4 h-4 mr-2" />
                Request Demo
              </Button>
            </Link>
            <Link to="/signup">
              <Button 
                data-testid="signup-btn"
                className="bg-[#004E89] hover:bg-[#003d6d] text-white rounded-full px-5 h-10 font-medium text-sm whitespace-nowrap"
              >
                <UserPlus className="w-4 h-4 mr-2" />
                Partner Sign Up
              </Button>
            </Link>
          </div>

          {/* Mobile Menu */}
          <Sheet open={isOpen} onOpenChange={setIsOpen}>
            <SheetTrigger asChild className="lg:hidden">
              <Button variant="ghost" size="icon" data-testid="mobile-menu-btn">
                <Menu className="h-6 w-6 text-slate-700" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[320px] bg-white overflow-y-auto">
              <div className="flex flex-col gap-6 mt-8 pb-8">
                {/* Mobile Navigation */}
                {Object.entries(navStructure).map(([key, section]) => (
                  <div key={key}>
                    <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3 px-4">
                      {section.label}
                    </p>
                    <div className="space-y-1">
                      {section.items.slice(0, 4).map((item) => {
                        const Icon = item.icon;
                        return (
                          <Link
                            key={item.path}
                            to={item.path}
                            onClick={() => setIsOpen(false)}
                            className="flex items-center gap-3 px-4 py-2 text-sm font-medium text-slate-600 hover:text-[#004E89] hover:bg-slate-50 rounded-lg"
                          >
                            <Icon className="w-4 h-4" />
                            {item.name}
                          </Link>
                        );
                      })}
                    </div>
                  </div>
                ))}

                {/* Mobile CTAs */}
                <div className="px-4 space-y-3 pt-4 border-t border-slate-100">
                  <Link to="/demo" onClick={() => setIsOpen(false)}>
                    <Button 
                      variant="outline"
                      className="w-full rounded-full py-3 border-[#004E89]/20 text-[#004E89]"
                    >
                      <Play className="w-4 h-4 mr-2" />
                      Request Demo
                    </Button>
                  </Link>
                  <Link to="/signup" onClick={() => setIsOpen(false)}>
                    <Button 
                      className="w-full bg-[#004E89] hover:bg-[#003d6d] text-white rounded-full py-3"
                    >
                      <UserPlus className="w-4 h-4 mr-2" />
                      Partner Sign Up
                    </Button>
                  </Link>
                </div>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
